"use client";
import { useEffect, useState } from "react";
import { initializeOneSignal, registerOneSignal, setupOneSignalNotificationHandler, getOneSignalUserId } from "@/lib/onesignal";
import { toast } from "sonner";

export default function OneSignalProvider({ children }: { children: React.ReactNode }) {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

    useEffect(() => {
        const setupOneSignal = async () => {
            const appId = process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID;
            if (!appId) {
                console.error("❌ OneSignal App ID is not configured. Please set NEXT_PUBLIC_ONESIGNAL_APP_ID in your environment variables.");
                toast.error("Push notifications are not configured");
                return;
            }

            if (!window.OneSignal) {
                console.log("⏳ Waiting for OneSignal SDK to load...");
                const checkInterval = setInterval(() => {
                    if (window.OneSignal) {
                        clearInterval(checkInterval);
                        initializeOneSignal(appId).then(() => {
                            setIsInitialized(true);
                            setupNotifications();
                        });
                    }
                }, 100);

                setTimeout(() => {
                    clearInterval(checkInterval);
                    if (!window.OneSignal) {
                        console.error("❌ OneSignal SDK failed to load after 15 seconds");
                    }
                }, 15000);

                return;
            }

            try {
                await initializeOneSignal(appId);
                setIsInitialized(true);
                setupNotifications();
            } catch (error) {
                console.error("❌ Error initializing OneSignal:", error);
                toast.error("Failed to initialize push notifications");
            }
        };

        const setupNotifications = async () => {
            if (!window.OneSignal) return;

            try {
                // Check notification permission status
                const permission = Notification.permission;
                console.log("🔔 Browser notification permission:", permission);
                
                if (permission === "default") {
                    console.log("💡 Notification permission not yet requested");
                } else if (permission === "denied") {
                    console.warn("⚠️ Notification permission denied by user");
                    toast.error("Please enable notifications in your browser settings to receive push notifications");
                } else if (permission === "granted") {
                    console.log("✅ Notification permission granted - system notifications will work");
                }

                // Set up notification handlers
                await setupOneSignalNotificationHandler((notification) => {
                    console.log("🔔 Notification received:", notification);
                    
                    // Check if tab is focused
                    const isTabFocused = document.hasFocus();
                    console.log("🔔 Tab focused:", isTabFocused);
                    
                    // This only fires when app is in foreground
                    // Background notifications are handled automatically by the service worker
                    // But we should only show toast if tab is actually focused
                    if (isTabFocused) {
                        try {
                            const audio = new Audio('/notification-sound.mp3');
                            audio.play().catch((e) => {
                                console.warn('Audio play was prevented by browser policy:', e);
                            });
                        } catch (error) {
                            console.warn("Error playing notification sound:", error);
                        }

                        const notificationData = notification.notification || notification;
                        const title = notificationData.title || "New Notification";
                        const body = notificationData.body || notificationData.message || "";
                        
                        // Only show toast if tab is focused (in-app notification)
                        // System notifications are shown automatically by the browser when tab is closed
                        toast(title, {
                            description: body,
                        });
                    } else {
                        console.log("🔔 Tab not focused - system notification should appear automatically");
                    }
                });

                // Register for push notifications
                const userId = await registerOneSignal();
                if (userId) {
                    setSubscriptionId(userId);
                    setIsSubscribed(true);
                    console.log("✅ OneSignal subscription ID:", userId);

                    // Check subscription status
                    try {
                        if (window.OneSignal && window.OneSignal.User && window.OneSignal.User.PushSubscription) {
                            const isOptedIn = window.OneSignal.User.PushSubscription.optedIn;
                            console.log("🔧 Push subscription status:", {
                                optedIn: isOptedIn,
                                userId: userId,
                                permission: Notification.permission
                            });
                            
                            if (isOptedIn && permission === "granted") {
                                console.log("✅ Push notifications are fully enabled - you'll receive notifications even when the tab is closed");
                            } else {
                                console.warn("⚠️ Push notifications may not work in background - check permission and subscription status");
                            }
                        }
                    } catch (e) {
                        console.warn("Could not check subscription status:", e);
                    }

                    await sendSubscriptionIdToBackend(userId);
                } else {
                    console.warn("⚠️ OneSignal registration failed or permission denied");
                    setIsSubscribed(false);
                }

                if (window.OneSignal && window.OneSignal.User && window.OneSignal.User.PushSubscription) {
                    window.OneSignal.User.PushSubscription.addEventListener("change", async () => {
                        const newUserId = await getOneSignalUserId();
                        if (newUserId && newUserId !== subscriptionId) {
                            setSubscriptionId(newUserId);
                            await sendSubscriptionIdToBackend(newUserId);
                        }
                    });
                }
            } catch (error) {
                console.error("❌ Error setting up notifications:", error);
            }
        };

        const getDeviceName = (): string => {
            if (typeof window === "undefined") return "Unknown Device";
            
            try {
                const userAgent = navigator.userAgent;
                const platform = navigator.platform;
                
                let browser = "Unknown Browser";
                if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) browser = "Chrome";
                else if (userAgent.includes("Firefox")) browser = "Firefox";
                else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) browser = "Safari";
                else if (userAgent.includes("Edg")) browser = "Edge";
                else if (userAgent.includes("Opera") || userAgent.includes("OPR")) browser = "Opera";
                
                let osName = platform;
                if (platform.includes("Win")) {
                    const winVersion = userAgent.match(/Windows NT (\d+\.\d+)/)?.[1];
                    osName = winVersion ? `Windows ${winVersion}` : "Windows";
                } else if (platform.includes("Mac")) {
                    const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace(/_/g, ".");
                    osName = macVersion ? `macOS ${macVersion}` : "macOS";
                } else if (platform.includes("Linux")) {
                    osName = "Linux";
                } else if (platform.includes("Android")) {
                    const androidVersion = userAgent.match(/Android (\d+\.\d+)/)?.[1];
                    osName = androidVersion ? `Android ${androidVersion}` : "Android";
                } else if (platform.includes("iPhone") || platform.includes("iPad")) {
                    const iosVersion = userAgent.match(/OS (\d+[._]\d+)/)?.[1]?.replace(/_/g, ".");
                    osName = iosVersion ? `iOS ${iosVersion}` : "iOS";
                }
                
                return `${osName} - ${browser}`;
            } catch (error) {
                console.warn("Error detecting device name:", error);
                return "Web Device";
            }
        };

        const registerDeviceWithBackend = async (playerId: string) => {
            try {
                const deviceName = getDeviceName();
                const platform = "web";
                const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || "1.0.0";

                console.log("🚀 Registering device with backend:", { playerId, platform, deviceName, appVersion });

                const response = await fetch("/api/onesignal/register-device", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        player_id: playerId,
                        platform: platform,
                        device_name: deviceName,
                        app_version: appVersion,
                    }),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("❌ Backend error registering device:", errorText);
                    throw new Error(`Failed to register device: ${errorText}`);
                }

                const data = await response.json();
                console.log("✅ Device registered successfully:", data);
                return data;
            } catch (error) {
                console.error("❌ Error registering device with backend:", error);
                return null;
            }
        };

        const sendSubscriptionIdToBackend = async (userId: string) => {
            try {
                console.log("🚀 Sending OneSignal subscription ID to backend:", userId);
                
                const deviceRegistrationResult = await registerDeviceWithBackend(userId);

                if (deviceRegistrationResult) {
                    console.log("✅ Device registered successfully with backend");
                } else {
                    console.warn("⚠️ Device registration failed, but continuing...");
                }

                try {
                    const response = await fetch("/api/onesignal_token", {
                        method: "PUT",
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ subscription_id: userId }),
                    });

                    if (!response.ok) {
                        console.warn("⚠️ Failed to send subscription ID to old endpoint");
                    } else {
                        console.log("✅ Subscription ID sent to old endpoint successfully");
                    }
                } catch (error) {
                    console.warn("⚠️ Error sending to old endpoint (non-critical):", error);
                }
            } catch (error) {
                console.error("❌ Error in subscription ID handling:", error);
            }
        };

        setupOneSignal();

        const checkSubscription = setInterval(async () => {
            if (window.OneSignal && isInitialized) {
                try {
                    const currentUserId = await getOneSignalUserId();
                    if (currentUserId && currentUserId !== subscriptionId) {
                        setSubscriptionId(currentUserId);
                        await sendSubscriptionIdToBackend(currentUserId);
                    }
                } catch (error) {
                    console.error("Error checking subscription:", error);
                }
            }
        }, 5 * 60 * 1000); 

        return () => {
            clearInterval(checkSubscription);
        };
    }, [isInitialized, subscriptionId]);

    return <>{children}</>;
}

